import type { TransformationGraph, DbtJobRunRow } from "@shared/types/transformations";
import { dbtModelCountsOf, dbtModelCountsSentence } from "@shared/types/dbtModelCounts";
import { gitRefTerm, repoShortName } from "./repoFileUrl";
import { phasePhrase, runOutcome, skipReasonWord } from "./runOutcome";

/**
 * The Transformations landing's status line and its one action bar (Session 11, P5;
 * ux-s11-transformations.md §2.2 and §2.5, binding). Pure: everything is derived from the graph's
 * `bundle` (the binding as it is now) and the newest row of `GET /transformations/runs?limit=1`,
 * never from `/instances/current/binding` (audited). The words of a run's outcome come from P6's
 * `runOutcome()`, so the status line, the bars and the History view say one thing.
 *
 * The line is split into parts (`lead`, `at`, `tail`); `statusLineText` joins them. Since the
 * owner's 2026-09-28 feedback the screen no longer shows this sentence: it shows the facts strip
 * (`landingFacts.ts`). The line still gives the strip its state (loading, and `data-state`) and is
 * what the polite region announces when a run in flight changes outcome.
 */

export type LandingState =
  | "loading"
  | "no_repo"
  | "no_run"
  | "runs_unreadable"
  | "queued"
  | "running"
  | "succeeded"
  | "tests_failed"
  | "failed"
  | "stopped"
  | "skipped"
  | "cancelled";

export interface LandingInput {
  /** The graph's `bundle`; undefined while the graph loads or when it could not be read. */
  bundle: TransformationGraph["bundle"] | undefined;
  graphState: "loading" | "ready" | "error";
  /** The newest transform step (`useDbtJobRuns(1)`), null when there is none. */
  newest: DbtJobRunRow | null;
  runsState: "loading" | "ready" | "error";
}

export interface StatusLine {
  state: LandingState;
  /**
   * True: "Repository {name} connected · Branch {ref} · …". False: "No dbt repository is
   * connected.". Null: the graph could not be read, so the line says only what the runs read knows.
   */
  connected: boolean | null;
  ref: string | null;
  /** The repository as `owner/repo` (`repoShortName`), null when unbound or on a host skyData does not know. */
  repoName: string | null;
  /** The words before the relative time ("last run succeeded"). */
  lead: string;
  /** The instant the relative time is of; null when the line has none. */
  at: string | null;
  /** What joins `lead` and the relative time: a space, or " · " after a count. */
  atJoin: string;
  /** The words after the relative time (" · 4 models · 11 tests passed"). */
  tail: string;
  /** True while the newest step is queued or running. */
  inFlight: boolean;
  /** True when the graph request failed: the line adds "The project facts could not be read." */
  graphUnreadable: boolean;
}

export const GRAPH_UNREADABLE_SENTENCE = "The project facts could not be read.";

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Models the step built; null when dbt reported none. Round 6, T5: the models that SUCCEEDED
 * (`modelsSucceeded`); a row written before the four counts keeps reported minus failed.
 */
export function builtModels(row: DbtJobRunRow): number | null {
  return dbtModelCountsOf(row)?.built ?? null;
}

/** " · 4 models · 11 tests passed · 1 warned" for a succeeded step (UX §2.2). */
function succeededTail(row: DbtJobRunRow): string {
  const parts: string[] = [];
  const built = builtModels(row);
  if (built !== null) parts.push(plural(built, "model", "models"));
  if (row.testsTotal === 0) {
    parts.push("no tests ran");
  } else if (row.testsSucceeded !== null) {
    const warned = row.testsWarned ?? 0;
    const passed = Math.max(0, row.testsSucceeded - warned);
    parts.push(`${plural(passed, "test", "tests")} passed`);
    if (warned > 0) parts.push(`${warned} warned`);
  }
  return parts.map((part) => ` · ${part}`).join("");
}

/** The run half of the line: what the newest step says, without the repository prefix. */
function runPart(row: DbtJobRunRow): Pick<StatusLine, "state" | "lead" | "at" | "atJoin" | "tail"> {
  const outcome = runOutcome(row);
  const finished = row.finishedAt ?? row.startedAt ?? row.createdAt;
  switch (outcome.kind) {
    case "queued":
      return {
        state: "queued",
        // T1: "queued: waiting for run {id}" while another dbt run holds the slot.
        lead: row.queuedBehindRunId ? `queued: waiting for run ${row.queuedBehindRunId}` : "queued",
        at: row.createdAt,
        atJoin: " ",
        tail: "",
      };
    case "running":
      return {
        state: "running",
        lead: "running now, started",
        at: row.runStartedAt ?? row.startedAt ?? row.createdAt,
        atJoin: " ",
        tail: "",
      };
    case "succeeded":
      return {
        state: "succeeded",
        lead: "last run succeeded",
        at: finished,
        atJoin: " ",
        tail: succeededTail(row),
      };
    case "tests_failed": {
      const built = builtModels(row);
      const failed = row.testsFailed ?? 0;
      const failedTests = failed > 0 ? `${plural(failed, "test", "tests")} failed` : "tests failed";
      return {
        state: "tests_failed",
        lead:
          built === null
            ? `last run: ${failedTests}`
            : `last run built ${plural(built, "model", "models")}, ${failedTests}`,
        at: finished,
        atJoin: " · ",
        tail: "",
      };
    }
    case "stopped":
      return {
        state: "stopped",
        lead: `last run stopped while ${phasePhrase(outcome.phase ?? "")}`,
        at: finished,
        atJoin: " ",
        tail: "",
      };
    case "skipped":
      return {
        state: "skipped",
        lead: `last run was skipped (${skipReasonWord(row.skippedReason)})`,
        at: null,
        atJoin: " ",
        tail: "",
      };
    case "cancelled":
      return {
        state: "cancelled",
        lead: "last run was cancelled",
        at: finished,
        atJoin: " ",
        tail: "",
      };
    case "failed": {
      // T5: "0 of 4 built: 2 failed, 2 skipped because a model they depend on failed", from the
      // four counts; a row written before them keeps today's line.
      const counts = dbtModelCountsOf(row);
      return {
        state: "failed",
        lead: "last run failed",
        at: finished,
        atJoin: " ",
        tail: counts?.exact ? ` · ${dbtModelCountsSentence(counts)}` : "",
      };
    }
  }
}

export function landingStatusLine(input: LandingInput): StatusLine {
  const { bundle, graphState, newest, runsState } = input;
  const inFlight = newest ? runOutcome(newest).inFlight : false;
  const base = {
    ref: bundle?.ref ?? null,
    repoName: repoShortName(bundle?.repoUrl ?? null),
    atJoin: " ",
    inFlight,
    graphUnreadable: graphState === "error",
  };

  if (graphState === "loading" || runsState === "loading") {
    return { ...base, state: "loading", connected: null, lead: "", at: null, tail: "" };
  }
  if (bundle && !bundle.repoBound) {
    return {
      ...base,
      state: "no_repo",
      connected: false,
      lead: "No dbt repository is connected.",
      at: null,
      tail: "",
    };
  }
  const connected = bundle ? true : null;
  if (runsState === "error") {
    return {
      ...base,
      state: "runs_unreadable",
      connected,
      lead: "the last run could not be read",
      at: null,
      tail: "",
    };
  }
  if (!newest) {
    return { ...base, state: "no_run", connected, lead: "no run yet", at: null, tail: "" };
  }
  return { ...base, connected, ...runPart(newest) };
}

/**
 * "Repository example-org/skydata-saas-dbt-test connected · Branch main · " when the binding is known; nothing
 * when it is not. Round 7 T-38: the spoken form of the facts strip names the repository (as
 * `owner/repo`, like the strip, never the address; none on a host skyData does not know) and gives the ref its word.
 */
function statusLinePrefix(line: StatusLine): string {
  if (line.connected !== true) return "";
  const repository = line.repoName
    ? `Repository ${line.repoName} connected`
    : "Repository connected";
  const ref = line.ref ? ` · ${gitRefTerm(line.ref)} ${line.ref}` : "";
  return `${repository}${ref} · `;
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * The whole line as text. `relative` turns `at` into "2 hours ago"; the screen passes the same
 * function it renders with, so this is exactly what a reader sees.
 */
export function statusLineText(line: StatusLine, relative: (iso: string) => string): string {
  if (line.state === "loading") return "";
  if (line.state === "no_repo") return line.lead;
  const at = line.at ? `${line.atJoin}${relative(line.at)}` : "";
  const body = `${line.lead}${at}${line.tail}`;
  const text = line.connected === true ? `${statusLinePrefix(line)}${body}` : capitalise(body);
  return line.graphUnreadable ? `${text}. ${GRAPH_UNREADABLE_SENTENCE}` : text;
}

/* ------------------------------------------------------------------------------------- *
 * The action bars (UX §2.5): at most one, first match wins
 * ------------------------------------------------------------------------------------- */

export type LandingBarKind =
  | "no-repo"
  | "clone"
  | "auth"
  | "deps"
  | "config"
  | "compile"
  | "models-failed"
  | "tests-failed"
  | "skipped-fixable";

export type LandingBarAction = "view-run" | "open-destination" | "show-models" | "show-tests";

export interface LandingBar {
  kind: LandingBarKind;
  intent: "warning" | "error" | "info";
  message: string;
  actions: LandingBarAction[];
  /** The run the bar is about ("View run"); null for the no-repository bar. */
  runId: string | null;
  /**
   * Round 6 (T2): the fix is on the Destination screen's Warehouse tab (the warehouse sign-in, a
   * Databricks destination), so "open-destination" reads "Open Destination" and opens that tab.
   * Absent: the fix is the repository, on the dbt repository tab.
   */
  warehouseFix?: true;
}

/** The skip reasons the customer can fix on the Destination screen (UX §2.5 row 9). */
const FIXABLE_SKIPS: ReadonlySet<string> = new Set(["no_dbt_repo_url", "databricks_not_supported"]);

/** "…" + " " + the cause's sentence, when the image classified one. */
function withCause(message: string, row: DbtJobRunRow): string {
  const sentence = row.cause?.sentence?.trim();
  return sentence ? `${message} ${sentence}` : message;
}

/** The bar for a skipped run: only a skip the customer can fix on the Destination screen. */
function skippedBar(newest: DbtJobRunRow, runId: string): LandingBar | null {
  if (newest.skippedReason && FIXABLE_SKIPS.has(newest.skippedReason)) {
    return {
      kind: "skipped-fixable",
      intent: "info",
      message: `The last run was skipped: ${skipReasonWord(newest.skippedReason)}.`,
      actions: ["open-destination"],
      runId,
      ...(newest.skippedReason === "databricks_not_supported"
        ? { warehouseFix: true as const }
        : {}),
    };
  }
  return null;
}

/** The bar for a failed run whose exit class names the step that stopped it, or null. */
function exitClassBar(newest: DbtJobRunRow, runId: string): LandingBar | null {
  switch (newest.exitClass) {
    case "clone":
      return {
        kind: "clone",
        intent: "error",
        message:
          "The last run could not clone the repository. Check the address, the branch and the access token.",
        actions: ["view-run", "open-destination"],
        runId,
      };
    case "auth":
      return {
        kind: "auth",
        intent: "error",
        message: "The last run could not sign in to the warehouse.",
        actions: ["view-run", "open-destination"],
        runId,
        warehouseFix: true,
      };
    case "deps":
      return {
        kind: "deps",
        intent: "error",
        message: "The last run could not install the project's packages (dbt deps).",
        actions: ["view-run"],
        runId,
      };
    case "config":
      return {
        kind: "config",
        intent: "error",
        message: withCause("The last run could not read its configuration.", newest),
        actions: ["view-run", "open-destination"],
        runId,
      };
    case "compile":
      return {
        kind: "compile",
        intent: "error",
        message: withCause("The last run could not compile the project.", newest),
        actions: ["view-run", "show-models"],
        runId,
      };
  }
  return null;
}

/** The bar for failed models or failed tests in a failed run, or null. */
function failedCountsBar(newest: DbtJobRunRow, runId: string): LandingBar | null {
  const modelsFailed = newest.modelsFailed ?? 0;
  if (modelsFailed > 0 || newest.exitClass === "run") {
    return {
      kind: "models-failed",
      intent: "error",
      message:
        modelsFailed > 0
          ? `${plural(modelsFailed, "model", "models")} failed in the last run.`
          : "The last run failed before any model finished.",
      actions: ["show-models"],
      runId,
    };
  }
  const testsFailed = newest.testsFailed ?? 0;
  if (testsFailed > 0 || newest.exitClass === "test") {
    return {
      kind: "tests-failed",
      intent: "warning",
      message:
        testsFailed > 0
          ? `${plural(testsFailed, "test", "tests")} failed in the last run.`
          : "Tests failed in the last run.",
      actions: ["show-tests"],
      runId,
    };
  }
  return null;
}

/**
 * The one bar the landing shows, or null. No bar for a healthy state, a run in flight, a skip
 * the customer cannot fix on the Destination screen, or warned tests: a warn is the author's own
 * decision, not an action.
 */
export function landingBar(input: LandingInput): LandingBar | null {
  const { bundle, newest } = input;
  if (bundle && !bundle.repoBound) {
    return {
      kind: "no-repo",
      intent: "warning",
      message:
        // Round 6 (W12): the place, in the navigation's words.
        "No dbt repository is connected. Transformations run once one is set under Settings > Destination > dbt repository.",
      actions: ["open-destination"],
      runId: null,
    };
  }
  if (!newest || runOutcome(newest).inFlight) return null;
  const runId = newest.runId;

  if (newest.stepStatus === "skipped") return skippedBar(newest, runId);
  if (newest.stepStatus !== "failed") return null;
  return exitClassBar(newest, runId) ?? failedCountsBar(newest, runId);
}

/* ------------------------------------------------------------------------------------- *
 * The view switch (UX §2.4): Models (default) · Tests · History · Transformations
 * ------------------------------------------------------------------------------------- */

export const LANDING_VIEWS = ["models", "tests", "history", "transformations"] as const;
export type LandingView = (typeof LANDING_VIEWS)[number];

/** `?view=`: absent or unknown is Models, the default (ruling §8.1). */
export function landingViewOf(value: string | null): LandingView {
  return (LANDING_VIEWS as readonly string[]).includes(value ?? "")
    ? (value as LandingView)
    : "models";
}
